// client/src/services/reliableSessionService.js
//
// Fetches/clears the server-side store of the "Current Session" call cards.
// The backend keeps this snapshot in Redis (same shape as the FE's in-memory
// ReliableCallManager) from the 3CX events it receives even while the browser
// is disconnected. Since the FE no longer persists call state locally, this is
// the sole source that restores cards on login / refresh / reconnect — and,
// being server-authoritative, it heals a card stuck on Ringing/Connected
// instead of resurrecting it the way the old localStorage copy did.

import { fetchWithAuth } from './apiClient';

const getApiUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:7080/api'
    : `/api`;

export const reliableSessionService = {
  /**
   * GET the reliable call-card snapshot for a user.
   * @returns {Promise<{calls: Array}|null>} snapshot or null on failure
   */
  async fetchSnapshot(username) {
    if (!username) return null;
    try {
      const response = await fetchWithAuth(`${getApiUrl()}/reliable-session/${username}`);
      // Distinguish "request failed" from "user genuinely has no calls". Without
      // this an error body parsed to `{}` looked identical to an empty snapshot,
      // so a failed heal was silently indistinguishable from a successful no-op.
      if (!response.ok) {
        console.warn(`reliableSessionService.fetchSnapshot HTTP ${response.status} for ${username}`);
        return null;
      }
      const data = await response.json();
      return data?.snapshot || { calls: [] };
    } catch (error) {
      console.warn('reliableSessionService.fetchSnapshot failed:', error?.message);
      return null;
    }
  },

  /**
   * DELETE the server-side snapshot (best-effort; backs "Clear Session").
   */
  async clearSnapshot(username) {
    if (!username) return;
    try {
      await fetchWithAuth(`${getApiUrl()}/reliable-session/${username}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.warn('reliableSessionService.clearSnapshot failed:', error?.message);
    }
  },
};
